import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, User } from 'firebase/auth';
import { UserRole } from '../shared/types/user-role';

interface CustomClaims {
  role?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth = getAuth();
  private router = inject(Router);
  private authInitializedResolve!: () => void;
  private authInitialized = new Promise<void>((resolve) => {
    this.authInitializedResolve = resolve;
  });

  user = signal<User | null>(null);
  isPending = signal<boolean>(false);
  isAdmin = signal<boolean>(false);

  constructor() {
    this.initAuthState();
  }

  private initAuthState() {
    onAuthStateChanged(this.auth, async (user) => {
      this.user.set(user);
      if (user) {
        const tokenResult = await user.getIdTokenResult();
        const claims = tokenResult.claims as CustomClaims;
        this.isAdmin.set(claims.role === UserRole.ADMIN);
      } else {
        this.isAdmin.set(false);
      }
      // Resolve the promise on first auth state change
      this.authInitializedResolve();
    });
  }

  async login(email: string, password: string): Promise<{ error?: { message: string } }> {
    this.isPending.set(true);

    try {
      const { user } = await signInWithEmailAndPassword(this.auth, email, password);

      // Check if user has admin role
      const tokenResult = await user.getIdTokenResult();
      const claims = tokenResult.claims as CustomClaims;

      if (claims.role !== UserRole.ADMIN) {
        await this.auth.signOut();
        this.isPending.set(false);
        return { error: { message: 'Access denied. Admin privileges required.' } };
      }

      this.user.set(user);
      this.isAdmin.set(true);
      this.isPending.set(false);
      return {};
    } catch (error: unknown) {
      this.isPending.set(false);
      const err = error as { code?: string; message?: string };

      if (err.code === 'auth/invalid-credential') {
        return { error: { message: 'Invalid email or password' } };
      }
      if (err.code === 'auth/too-many-requests') {
        return { error: { message: 'Too many attempts. Please try again later.' } };
      }

      return { error: { message: err.message || 'Login failed' } };
    }
  }

  async logout(): Promise<void> {
    await this.auth.signOut();
    this.user.set(null);
    this.isAdmin.set(false);
    this.router.navigate(['/login']);
  }

  async getToken(): Promise<string | null> {
    const user = this.user();
    if (!user) return null;
    return user.getIdToken();
  }

  async checkSession(): Promise<void> {
    this.isPending.set(true);
    // Wait for Firebase to restore auth state on page refresh
    await this.authInitialized;
    this.isPending.set(false);
  }
}
